"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useUnifiedBalance } from "@/contexts/UnifiedBalanceContext";
import { usePolymarketBalanceContext } from "@/contexts/PolymarketBalanceContext";
import { authFetch } from "@/lib/authFetch";
import { useT } from "@/hooks/useT";
import { FiArrowRight, FiRefreshCw } from "react-icons/fi";
import { usePredictEnabled } from "@/hooks/usePredictEnabled";

function formatUsd(n: number | null): string {
  if (n === null) return "—";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
}

interface UnifiedBalanceCardProps {
  onMoveFunds?: () => void;
}

export default function UnifiedBalanceCard({ onMoveFunds }: UnifiedBalanceCardProps) {
  const t = useT();
  const { totalAvailable, polymarket, predictfun, kalshi, hyperliquid, loading } = useUnifiedBalance();
  const predictEnabled = usePredictEnabled();
  const { safeAddress, depositWalletAddress, refetch: refetchPoly } = usePolymarketBalanceContext();
  const [wrapping, setWrapping] = useState(false);
  const [wrapError, setWrapError] = useState<string | null>(null);
  const [wrappingNative, setWrappingNative] = useState(false);
  const [wrapNativeError, setWrapNativeError] = useState<string | null>(null);
  const [unwrapping, setUnwrapping] = useState(false);
  const [unwrapError, setUnwrapError] = useState<string | null>(null);

  const unwrappedUsdce = polymarket.bridged ?? 0;
  const nativeUsdc = polymarket.usdcNative ?? 0;
  const pusdBalance = polymarket.pusd ?? 0;
  // Either custody works — backend ramifies per wallet_type. Without this,
  // deposit-wallet users would never see the wrap/unwrap buttons even when
  // their wallet holds USDC.e.
  const tradingAddress = depositWalletAddress || safeAddress;
  const showWrapButton = !!tradingAddress && unwrappedUsdce > 0.01;
  // Polymarket bridge floor is $2 USD — anything below is dropped server-side
  // with no auto-refund. Hide the button until the user has enough to convert,
  // and surface the floor as helper text so they know it isn't broken.
  const showWrapNativeButton = !!tradingAddress && nativeUsdc >= 2;
  const showNativeBelowMinHint = !!tradingAddress && nativeUsdc > 0.01 && nativeUsdc < 2;
  const showUnwrapButton = !!tradingAddress && pusdBalance > 0.01;

  // Tell the backend a wrap finished from this user's browser so the bell
  // row + Telegram push fire even when the manual button is what triggered
  // the wrap (the deposit-monitor only enqueues notifications when *it*
  // performs the wrap; manual clicks bypass it).
  const ackWrap = (asset: "USDC" | "USDC.e", amountStr?: string, txHash?: string) => {
    const amountNum = parseFloat(amountStr || "0");
    if (!Number.isFinite(amountNum) || amountNum <= 0) return;
    toast.success(t("bridge.balance.toastConverted", { amount: amountNum.toFixed(2), asset }), {
      duration: 4000,
    });
    authFetch("/api/wallet/deposit-wrapped-ack", {
      method: "POST",
      body: JSON.stringify({ asset, amountUsd: amountNum, ...(txHash ? { txHash } : {}) }),
    }).catch(() => {
      // Bell row is nice-to-have; toast already covers immediate feedback.
    });
  };

  // Wrap/unwrap handlers now hit a unified backend endpoint that branches
  // per-custody (Safe via Safe.execTransaction, deposit wallet via WALLET
  // batch). Frontend doesn't need to know — same call shape for both.
  async function callBackend(path: string, body: Record<string, unknown> = {}) {
    const r = await authFetch(path, { method: "POST", body: JSON.stringify(body) });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data.success) {
      throw new Error(data?.error || `Request failed (${r.status})`);
    }
    return data;
  }

  const handleWrap = async () => {
    if (!tradingAddress || wrapping) return;
    setWrapping(true);
    setWrapError(null);
    try {
      const r = await callBackend("/api/wallet/wrap-to-pusd", { asset: "usdce" });
      ackWrap("USDC.e", r.amountIn, r.txHash);
      refetchPoly();
    } catch (err) {
      setWrapError(err instanceof Error ? err.message : "Wrap failed");
    } finally {
      setWrapping(false);
    }
  };

  const handleWrapNative = async () => {
    if (!tradingAddress || wrappingNative) return;
    setWrappingNative(true);
    setWrapNativeError(null);
    try {
      const r = await callBackend("/api/wallet/wrap-to-pusd", { asset: "native" });
      // Bridge route is async — the tx submitted but pUSD only arrives once
      // Polymarket's bridge service processes the inflow (~1-5 min). Show a
      // dedicated "pending" toast instead of the standard "converted" one
      // so the user knows to wait.
      if (r.pending || r.route === "bridge") {
        const amt = parseFloat(r.amountIn || "0");
        toast.success(
          amt > 0
            ? `Sent $${amt.toFixed(2)} USDC to Polymarket bridge. pUSD will land in ~1-5 minutes.`
            : "Sent to Polymarket bridge. pUSD will land in ~1-5 minutes.",
          { duration: 6000 },
        );
      } else {
        ackWrap("USDC", r.amountIn, r.txHash);
      }
      refetchPoly();
    } catch (err) {
      setWrapNativeError(err instanceof Error ? err.message : "Wrap failed");
    } finally {
      setWrappingNative(false);
    }
  };

  const handleUnwrap = async () => {
    if (!tradingAddress || unwrapping) return;
    setUnwrapping(true);
    setUnwrapError(null);
    try {
      await callBackend("/api/wallet/unwrap-from-pusd", {});
      refetchPoly();
    } catch (err) {
      setUnwrapError(err instanceof Error ? err.message : "Unwrap failed");
    } finally {
      setUnwrapping(false);
    }
  };

  return (
    <div className="rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] p-5">
      {/* Total */}
      <div className="mb-4">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
          {t("bridge.balance.available")}
        </p>
        <p className={`text-2xl font-semibold text-gray-900 dark:text-white ${loading ? "animate-pulse" : ""}`}>
          {/* Predict.fun included in the headline only when ENABLE_PREDICT; otherwise subtracted out (hidden). */}
          {formatUsd(Math.max(0, (totalAvailable ?? 0) - (predictEnabled ? 0 : (predictfun.usdt ?? 0))))}
        </p>
      </div>

      {/* Per-venue breakdown — same visual language as PortfolioProfileBar:
          stacked horizontal bar split by venue (brand colors) + dot-prefixed
          rows with amounts. Predict.fun is hidden until production rollout. */}
      {(() => {
        const pm = polymarket.usdc ?? 0;
        const ks = kalshi.usdc ?? 0;
        const hl = hyperliquid.usdc ?? 0;
        const pf = predictEnabled ? (predictfun.usdt ?? 0) : 0;
        const total = pm + ks + hl + pf;
        const pmPct = total > 0 ? (pm / total) * 100 : 0;
        const ksPct = total > 0 ? (ks / total) * 100 : 0;
        const hlPct = total > 0 ? (hl / total) * 100 : 0;
        const pfPct = total > 0 ? (pf / total) * 100 : 0;

        return (
          <>
            {/* Stacked bar */}
            <div className="h-2.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] overflow-hidden flex mb-3">
              {pmPct > 0 && <div className="h-full bg-[#2E5CFF]" style={{ width: `${pmPct}%` }} />}
              {ksPct > 0 && <div className="h-full bg-[#09C285]" style={{ width: `${ksPct}%` }} />}
              {hlPct > 0 && <div className="h-full bg-[#97FCE4]" style={{ width: `${hlPct}%` }} />}
              {pfPct > 0 && <div className="h-full bg-[#8B5CF6]" style={{ width: `${pfPct}%` }} />}
            </div>

            {/* Breakdown rows */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs mb-4">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-[5px] bg-[#2E5CFF]" />
                <span className="font-semibold text-gray-900 dark:text-white">
                  <span className="sm:hidden">PM</span>
                  <span className="hidden sm:inline">Polymarket</span>
                </span>
                <span className="text-gray-600 dark:text-gray-300 tabular-nums">{formatUsd(pm)}</span>
              </div>
              <span className="text-gray-300 dark:text-gray-600">·</span>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-[5px] bg-[#09C285]" />
                <span className="font-semibold text-gray-900 dark:text-white">Kalshi</span>
                <span className="text-gray-600 dark:text-gray-300 tabular-nums">{formatUsd(ks)}</span>
              </div>
              {hl > 0 && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-[5px] bg-[#97FCE4]" />
                    <span className="font-semibold text-gray-900 dark:text-white">
                      <span className="sm:hidden">HL</span>
                      <span className="hidden sm:inline">Hyperliquid</span>
                    </span>
                    <span className="text-gray-600 dark:text-gray-300 tabular-nums">{formatUsd(hl)}</span>
                  </div>
                </>
              )}
              {pf > 0 && (
                <>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-[5px] bg-[#8B5CF6]" />
                    <span className="font-semibold text-gray-900 dark:text-white">Predict.fun</span>
                    <span className="text-gray-600 dark:text-gray-300 tabular-nums">{formatUsd(pf)}</span>
                  </div>
                </>
              )}
            </div>
          </>
        );
      })()}

      {/* Manual wrap button — surfaces only when Safe holds unwrapped USDC.e that
          auto-wrap hasn't converted yet (e.g. just landed, retry after a failure). */}
      {showWrapButton && (
        <button
          onClick={handleWrap}
          disabled={wrapping}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 mb-3 rounded-[5px] text-xs font-medium
            bg-gray-50 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400
            hover:bg-gray-100 dark:hover:bg-[#0a0a0b]
            border border-gray-200 dark:border-[#1A1A1A]
            transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiRefreshCw className={`w-3 h-3 ${wrapping ? "animate-spin" : ""}`} />
          {wrapping
            ? t("bridge.balance.converting")
            : t("bridge.balance.convertUsdce", { amount: formatUsd(unwrappedUsdce) })}
        </button>
      )}
      {wrapError && (
        <p className="text-xs text-red-500 mb-3">{t("bridge.balance.wrapFailed", { error: wrapError })}</p>
      )}

      {/* Native USDC → pUSD conversion. On deposit-wallet accounts the wrap
          routes through Polymarket's bridge service (their on-chain
          CollateralOnramp rejects native: `_asset must be USDC.e`). The user
          just sees one button; backend handles the bridge round-trip. Async
          — pUSD lands in 1-5 min once Polymarket processes the inflow. */}
      {showWrapNativeButton && (
        <button
          onClick={handleWrapNative}
          disabled={wrappingNative}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 mb-3 rounded-[5px] text-xs font-medium
            bg-gray-50 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400
            hover:bg-gray-100 dark:hover:bg-[#0a0a0b]
            border border-gray-200 dark:border-[#1A1A1A]
            transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiRefreshCw className={`w-3 h-3 ${wrappingNative ? "animate-spin" : ""}`} />
          {wrappingNative
            ? t("bridge.balance.converting")
            : t("bridge.balance.convertUsdc", { amount: formatUsd(nativeUsdc) })}
        </button>
      )}
      {showNativeBelowMinHint && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3 px-1">
          ${nativeUsdc.toFixed(2)} USDC on your wallet — Polymarket&apos;s bridge requires a $2 minimum to auto-convert to pUSD.
        </p>
      )}
      {wrapNativeError && (
        <p className="text-xs text-red-500 mb-3">{t("bridge.balance.wrapFailed", { error: wrapNativeError })}</p>
      )}

      {/* Manual unwrap button — for users who want to move pUSD back to USDC.e
          without triggering a full withdraw. Relayer-paid. */}
      {showUnwrapButton && (
        <button
          onClick={handleUnwrap}
          disabled={unwrapping}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 mb-3 rounded-[5px] text-xs font-medium
            bg-gray-50 dark:bg-[#0f0f12] text-gray-600 dark:text-gray-400
            hover:bg-gray-100 dark:hover:bg-[#0a0a0b]
            border border-gray-200 dark:border-[#1A1A1A]
            transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiRefreshCw className={`w-3 h-3 ${unwrapping ? "animate-spin" : ""}`} />
          {unwrapping
            ? t("bridge.balance.converting")
            : t("bridge.balance.convertPusdBack", { amount: formatUsd(pusdBalance) })}
        </button>
      )}
      {unwrapError && (
        <p className="text-xs text-red-500 mb-3">{t("bridge.balance.unwrapFailed", { error: unwrapError })}</p>
      )}

      {/* Move Funds button */}
      {onMoveFunds && (
        <button
          onClick={onMoveFunds}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-[5px] text-sm font-medium
            bg-gray-50 dark:bg-[#0f0f12] text-gray-700 dark:text-gray-300
            hover:bg-gray-100 dark:hover:bg-[#0a0a0b]
            border border-gray-200 dark:border-[#1A1A1A]
            transition-colors"
        >
          {t("bridge.balance.moveFunds")}
          <FiArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
